import type { VfxTier } from '@/utils/vfxManager';

export function createFieldPoints(count: number) {
  let seed = 731;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  return Array.from({ length: count }, (_, index) => {
    const radius = Math.sqrt(random());
    const angle = radius * 6.8 + (index % 3) * Math.PI * 2 / 3;
    return {
      x: Math.cos(angle) * radius + (random() - .5) * .13,
      y: Math.sin(angle) * radius + (random() - .5) * .13,
      depth: random(),
      size: .45 + random() * 1.05,
    };
  });
}

interface PointFieldOptions {
  tier: VfxTier;
  color: string;
  motion: boolean;
}

/** Один кадр в покое; кадры во время взаимодействия прекращаются после плавного затухания. */
export function mountPointField(canvas: HTMLCanvasElement, options: PointFieldOptions): () => void {
  const doc = canvas.ownerDocument;
  const view = doc.defaultView;
  const ctx = canvas.getContext('2d');
  if (!view || !ctx) return () => {};

  const reduced = view.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = view.matchMedia('(hover: hover) and (pointer: fine)');
  let width = 0;
  let height = 0;
  let frame: number | null = null;
  let enabled = false;
  let listening = false;
  let lastTime = 0;
  let points = createFieldPoints(0);
  const current = { x: 0, y: 0, scroll: 0 };
  const target = { ...current };

  const draw = () => {
    ctx.clearRect(0, 0, width, height);
    const radius = Math.min(width * .62, height * .86);
    const rotation = -.35 + current.scroll * .52 + current.x * .07;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const centerX = width * .73 + current.x * 18;
    const centerY = height * .46 + current.y * 14 - current.scroll * height * .1;
    ctx.fillStyle = options.color;
    for (const point of points) {
      const x = centerX + (point.x * cos - point.y * sin) * radius + current.x * point.depth * 16;
      const y = centerY + (point.x * sin + point.y * cos) * radius * .62 + current.y * point.depth * 12;
      ctx.globalAlpha = .1 + point.depth * .38;
      ctx.beginPath();
      ctx.arc(x, y, point.size, 0, Math.PI * 2);
      ctx.fill();
      if (point.depth > .93) {
        ctx.globalAlpha = .035;
        ctx.beginPath();
        ctx.arc(x, y, point.size * 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  };

  const cancel = () => {
    if (frame !== null) view.cancelAnimationFrame(frame);
    frame = null;
  };

  const animate = (time: number) => {
    frame = null;
    const ease = 1 - Math.exp(-Math.min(time - lastTime || 16, 64) / 110);
    lastTime = time;
    let unsettled = false;
    for (const key of ['x', 'y', 'scroll'] as const) {
      current[key] += (target[key] - current[key]) * ease;
      if (Math.abs(target[key] - current[key]) > .001) unsettled = true;
      else current[key] = target[key];
    }
    draw();
    canvas.dataset.motion = unsettled ? 'moving' : 'idle';
    if (unsettled) frame = view.requestAnimationFrame(animate);
  };

  const requestDraw = () => {
    if (!enabled || doc.hidden || frame !== null) return;
    if (Object.keys(target).every(key => target[key as keyof typeof target] === current[key as keyof typeof current])) return;
    lastTime = view.performance.now();
    frame = view.requestAnimationFrame(animate);
  };

  const readScroll = () => {
    const scroll = Math.max(0, view.scrollY);
    target.scroll = scroll / (scroll + Math.max(1, view.innerHeight));
  };
  const onScroll = () => { readScroll(); requestDraw(); };
  const onPointer = (event: PointerEvent) => {
    if (!pointer.matches || event.pointerType === 'touch') return;
    target.x = Math.max(-1, Math.min(1, event.clientX / Math.max(1, view.innerWidth) * 2 - 1));
    target.y = Math.max(-1, Math.min(1, event.clientY / Math.max(1, view.innerHeight) * 2 - 1));
    requestDraw();
  };
  const resetPointer = () => { target.x = 0; target.y = 0; requestDraw(); };
  const onPointerOut = (event: PointerEvent) => { if (!event.relatedTarget) resetPointer(); };

  const removeInteraction = () => {
    view.removeEventListener('scroll', onScroll);
    view.removeEventListener('pointermove', onPointer);
    view.removeEventListener('pointerout', onPointerOut);
    view.removeEventListener('blur', resetPointer);
    listening = false;
  };
  const configure = () => {
    cancel();
    removeInteraction();
    enabled = options.motion && options.tier !== 'off' && !reduced.matches;
    target.x = 0;
    target.y = 0;
    target.scroll = 0;
    if (enabled) readScroll();
    Object.assign(current, target);
    if (doc.hidden) { canvas.dataset.motion = 'hidden'; return; }
    canvas.dataset.motion = enabled ? 'idle' : 'static';
    draw();
    if (!enabled) return;
    view.addEventListener('scroll', onScroll, { passive: true });
    if (pointer.matches) {
      view.addEventListener('pointermove', onPointer, { passive: true });
      view.addEventListener('pointerout', onPointerOut, { passive: true });
      view.addEventListener('blur', resetPointer);
    }
    listening = true;
  };
  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    const compact = width <= 768;
    const dpr = Math.min(view.devicePixelRatio || 1, compact ? 1 : 1.5, Math.sqrt(3_000_000 / (width * height)));
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    points = createFieldPoints(compact ? 100 : options.tier === 'max' ? 320 : 190);
    configure();
  };

  resize();
  view.addEventListener('resize', resize, { passive: true });
  doc.addEventListener('visibilitychange', configure);
  reduced.addEventListener('change', configure);
  pointer.addEventListener('change', configure);
  return () => {
    cancel();
    if (listening) removeInteraction();
    view.removeEventListener('resize', resize);
    doc.removeEventListener('visibilitychange', configure);
    reduced.removeEventListener('change', configure);
    pointer.removeEventListener('change', configure);
  };
}
